import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaLinkedin, FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { Container, theme } from '../../styles/GlobalStyles';

const FooterContainer = styled.footer`
  background: ${theme.colors.background};
  color: ${theme.colors.text};
  padding: 60px 0 20px;
  border-top: 1px solid ${theme.colors.border};
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 40px;
  margin-bottom: 40px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FooterColumn = styled.div`
  @media (max-width: ${theme.breakpoints.tablet}) {
    &:first-child {
      grid-column: 1 / -1;
    }
  }
`;

const CompanyInfo = styled.div`
  @media (max-width: ${theme.breakpoints.tablet}) {
    text-align: center;
    margin-bottom: 20px;
  }
`;

const Logo = styled.h3`
  font-size: 1.8rem;
  font-weight: 800;
  color: ${theme.colors.accent};
  margin-bottom: 20px;
`;

const CompanyDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${theme.colors.textSecondary};
  margin-bottom: 20px;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  color: ${theme.colors.textSecondary};

  svg {
    color: ${theme.colors.accent};
    font-size: 1rem;
  }
`;

const ColumnTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 20px;
  color: ${theme.colors.text};
`;

const FooterLinks = styled.ul`
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FooterLink = styled(Link)`
  color: ${theme.colors.textSecondary};
  font-size: 0.95rem;
  transition: all ${theme.transitions.fast};
  
  &:hover {
    color: ${theme.colors.accent};
    padding-left: 5px;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 20px;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${theme.colors.accent};
  color: ${theme.colors.text};
  border-radius: 50%;
  font-size: 1.2rem;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.accentHover};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.glow};
  }
`;

const FooterBottom = styled.div`
  border-top: 1px solid ${theme.colors.border};
  padding-top: 20px;
  text-align: center;
  font-size: 0.9rem;
  color: ${theme.colors.textMuted};
`;

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          <FooterColumn>
            <CompanyInfo>
              <Logo>CYHY360</Logo>
              <CompanyDescription>
                Leading cybersecurity consulting firm specializing in governance, risk management, 
                and compliance solutions. Protecting organizations through expert security services 
                and cutting-edge technologies.
              </CompanyDescription>
              <ContactInfo>
                <ContactItem>
                  <FaPhone />
                  <span>+1 (555) 123-4567</span>
                </ContactItem>
                <ContactItem>
                  <FaEnvelope />
                  <span>info@cyhy360.com</span>
                </ContactItem>
                <ContactItem>
                  <FaMapMarkerAlt />
                  <span>123 Security Blvd, Cyber City, CC 12345</span>
                </ContactItem>
              </ContactInfo>
              <SocialLinks>
                <SocialLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer">
                  <FaLinkedin />
                </SocialLink>
              </SocialLinks>
            </CompanyInfo>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Company</ColumnTitle>
            <FooterLinks>
              <li><FooterLink to="/services">Services</FooterLink></li>
              <li><FooterLink to="/about">About</FooterLink></li>
              <li><FooterLink to="/career">Career</FooterLink></li>
              <li><FooterLink to="/contact">Contact</FooterLink></li>
            </FooterLinks>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Products</ColumnTitle>
            <FooterLinks>
              <li><FooterLink to="/services/risk-assessment">Risk Assessments</FooterLink></li>
              <li><FooterLink to="/compliance/iso-27001">ISO 27001</FooterLink></li>
              <li><FooterLink to="/technologies/database-security">Database Security</FooterLink></li>
              <li><FooterLink to="/technologies/dlp">Data Leakage Prevention</FooterLink></li>
            </FooterLinks>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Services</ColumnTitle>
            <FooterLinks>
              <li><FooterLink to="/vulnerability/penetration-testing">Penetration Testing</FooterLink></li>
              <li><FooterLink to="/services/risk-assessment">Risk Assessments</FooterLink></li>
              <li><FooterLink to="/compliance/pci-dss">PCI DSS Compliance</FooterLink></li>
              <li><FooterLink to="/managed-services/soc">SOC Services</FooterLink></li>
            </FooterLinks>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Resources</ColumnTitle>
            <FooterLinks>
              <li><FooterLink to="/blog">Blog</FooterLink></li>
              <li><FooterLink to="/case-studies">Case Studies</FooterLink></li>
              <li><FooterLink to="/whitepapers">Whitepapers</FooterLink></li>
              <li><FooterLink to="/webinars">Webinars</FooterLink></li>
            </FooterLinks>
          </FooterColumn>
        </FooterContent>

        <FooterBottom>
          <p>&copy; {currentYear} CYHY360. All rights reserved. | Privacy Policy | Terms of Service</p>
        </FooterBottom>
      </Container>
    </FooterContainer>
  );
};

export default Footer;

